import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth, useCart } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Heart, ShoppingBag, Shield, Truck, RefreshCw, ChevronRight, Coins, Share2, Check, MapPin, Sparkles } from "lucide-react";
import ProductReviews from "../components/ProductReviews";
import RelatedProducts from "../components/RelatedProducts";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const { refreshCart, wishlist, refreshWishlist, addToGuestCart } = useCart();
  const [p, setP] = useState(null);
  const [size, setSize] = useState("");
  const [imgIdx, setImgIdx] = useState(0);
  const [adding, setAdding] = useState(false);
  const [rate, setRate] = useState(null);
  const [copied, setCopied] = useState(false);
  const [pincode, setPincode] = useState("");
  const [checking, setChecking] = useState(false);
  const [delivery, setDelivery] = useState(null); // {serviceable, etd, cod_charges, freight_charges, total}
  const [deliveryErr, setDeliveryErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/products/${id}`).then(r => {
      setP(r.data);
      // Auto-select the first size so customers never get stopped at "please choose a size".
      // They can still tap a different size before adding to cart.
      setSize((r.data.sizes && r.data.sizes[0]) || "");
      setImgIdx(0);
    });
    axios.get(`${API}/gold-rate`).then(r => setRate(r.data));
  }, [id]);

  if (!p) return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-[#8A8480]">Loading…</div>;

  const isWished = wishlist.some(w => w.product_id === p.product_id);
  const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;
  const discount = p.mrp > p.price ? Math.round(((p.mrp - p.price) / p.mrp) * 100) : 0;

  const addToCart = async () => {
    // Never block a customer on size: silently fall back to the first size if
    // they haven't picked one. Auto-selection at load time already covers this
    // for 99% of cases; this is the belt-and-braces for edge cases.
    const chosenSize = size || (p.sizes && p.sizes[0]) || null;
    setAdding(true);
    try {
      if (user) {
        await axios.post(`${API}/cart/items`, { product_id: p.product_id, quantity: 1, size: chosenSize });
        await refreshCart();
      } else {
        await addToGuestCart({ product_id: p.product_id, quantity: 1, size: chosenSize });
      }
      toast.success("Added to your cart");
    } finally { setAdding(false); }
  };

  const buyNow = async () => {
    await addToCart();
    navigate("/checkout");
  };

  const toggleWish = async () => {
    if (!user) { login(); return; }
    await axios.post(`${API}/wishlist/toggle`, { product_id: p.product_id });
    refreshWishlist();
    toast.success(isWished ? "Removed from wishlist" : "Added to wishlist");
  };

  const checkDelivery = async () => {
    setDeliveryErr("");
    setDelivery(null);
    if (!/^\d{6}$/.test(pincode)) {
      setDeliveryErr("Please enter a valid 6-digit pincode");
      return;
    }
    setChecking(true);
    try {
      // Estimate weight in kg — round up to 0.1 kg min
      const weightKg = Math.max(0.1, (Number(p.weight) || 100) / 1000);
      const r = await axios.get(`${API}/shipping/serviceability`, {
        params: { pincode, weight: weightKg, cod: 1 },
      });
      if (!r.data.serviceable) {
        setDeliveryErr("Sorry, delivery isn't available to this pincode yet. Try another.");
      } else {
        setDelivery(r.data);
      }
    } catch (e) {
      setDeliveryErr(e.response?.data?.detail || "Couldn't reach delivery service. Try again.");
    } finally {
      setChecking(false);
    }
  };

  const share = async () => {
    // Backend share endpoint carries OG meta so the preview looks aesthetic when pasted.
    const shareUrl = `${window.location.origin}/api/share/${p.product_id}`;
    const text = `Hey, look at this beautiful ${p.purity || ""} ${p.category || "piece"} by Sumanya — ${inr(p.price)} ✨`;
    if (navigator.share) {
      try {
        await navigator.share({ title: `${p.name} · Sumanya`, text, url: shareUrl });
        return;
      } catch (e) {
        if (e?.name === "AbortError") return; // user cancelled
      }
    }
    try {
      await navigator.clipboard.writeText(`${text}\n${shareUrl}`);
      setCopied(true);
      toast.success("Link copied — paste it in any chat, WhatsApp will show a preview");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy the link");
    }
  };

  const images = p.images?.length ? p.images : [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="product-detail-page">
      {/* breadcrumb */}
      <nav className="flex items-center text-xs text-[#8A8480] gap-1 mb-6">
        <Link to="/" className="hover:text-[#8B1E28]">Home</Link>
        <ChevronRight className="h-3 w-3" />
        <Link to={`/products/${p.category}`} className="hover:text-[#8B1E28] capitalize">{p.category}</Link>
        <ChevronRight className="h-3 w-3" />
        <span className="text-[#2C2A29]">{p.name}</span>
      </nav>

      <div className="grid lg:grid-cols-2 gap-12">
        <div>
          <div className="aspect-square bg-[#FFF5F3] rounded-3xl overflow-hidden border border-[#EAE3D9]">
            {!images.length ? (
              // Decorative fallback until the owner uploads a photo — matches the
              // ProductCard "Photo coming soon" treatment for consistency.
              <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-[#FFF5F3] to-[#F5E1D6] text-[#C5A059]" data-testid="product-main-noimg">
                <svg width="72" height="72" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                  <path d="M12 2 L14 10 L22 12 L14 14 L12 22 L10 14 L2 12 L10 10 Z" />
                </svg>
                <span className="text-xs uppercase tracking-widest mt-3 text-[#8A8480]">Photo coming soon</span>
              </div>
            ) : /\.(mp4|mov|webm|m4v)(\?|$)/i.test(images[imgIdx] || "") ? (
              <video src={images[imgIdx]} className="w-full h-full object-cover" controls autoPlay muted loop playsInline data-testid="product-main-video" />
            ) : (
              <img src={images[imgIdx]} alt={p.name} className="w-full h-full object-cover" data-testid="product-main-image" />
            )}
          </div>
          {images.length > 1 && (
            <div className="flex gap-3 mt-4 flex-wrap">
              {images.map((img, i) => (
                <button key={i} onClick={() => setImgIdx(i)}
                  className={`h-20 w-20 rounded-xl overflow-hidden border-2 ${i === imgIdx ? "border-[#8B1E28]" : "border-transparent"}`}
                  data-testid={`thumb-${i}`}>
                  {/\.(mp4|mov|webm|m4v)(\?|$)/i.test(img) ? (
                    <video src={img} className="w-full h-full object-cover" muted />
                  ) : (
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          {p.collection && <p className="label-small text-[#C5A059] mb-3">{p.collection}</p>}
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#2C2A29] leading-tight" data-testid="product-name">{p.name}</h1>

          <div className="mt-5 flex items-baseline gap-3">
            <span className="text-3xl font-semibold text-[#2C2A29]" data-testid="product-price">{inr(p.price)}</span>
            {p.mrp > p.price && (
              <>
                <span className="text-lg text-[#8A8480] line-through">{inr(p.mrp)}</span>
                <span className="text-sm font-medium text-[#2E7D32]">{discount}% off</span>
              </>
            )}
          </div>
          <p className="text-xs text-[#8A8480] mt-1">Inclusive of all taxes · Free shipping</p>

          {p.is_diamond && p.price_breakdown?.diamond_saved > 0 && (
            <div
              className="mt-3 inline-flex items-center gap-2 bg-[#8B1E28] text-white text-xs font-medium px-3 py-1.5 rounded-full shadow-sm"
              data-testid="diamond-savings-badge"
            >
              <Sparkles className="h-3.5 w-3.5" />
              You save ₹{p.price_breakdown.diamond_saved.toLocaleString("en-IN")} on the diamond ({p.price_breakdown.diamond_discount_pct}% off)
            </div>
          )}

          {/* spec card */}
          <div className={`mt-7 grid ${p.is_diamond ? "grid-cols-4" : (p.hide_weight ? "grid-cols-2" : "grid-cols-3")} gap-3 p-5 bg-[#FFF5F3]/70 rounded-2xl border border-[#EAE3D9]`} data-testid="product-specs">
            <div>
              <div className="label-small text-[#8A8480]">Purity</div>
              <div className="text-base font-semibold text-[#2C2A29] mt-1">{p.purity}</div>
            </div>
            {!p.hide_weight && (
              <div data-testid="spec-weight">
                <div className="label-small text-[#8A8480]">{p.is_diamond ? "Net gold" : "Weight"}</div>
                <div className="text-base font-semibold text-[#2C2A29] mt-1">{p.weight}g</div>
              </div>
            )}
            {p.is_diamond && (
              <div data-testid="spec-diamond">
                <div className="label-small text-[#8A8480]">Diamond</div>
                <div className="text-base font-semibold text-[#2C2A29] mt-1">{p.diamond_weight_cts} ct</div>
              </div>
            )}
            <div>
              <div className="label-small text-[#8A8480]">Category</div>
              <div className="text-base font-semibold text-[#2C2A29] mt-1 capitalize">{(p.category || "").replace(/-/g, " ")}</div>
            </div>
          </div>

          {/* Live price breakdown */}
          {p.price_breakdown && !p.hide_price_breakup && (
            <div className="mt-4 bg-white border border-[#EAE3D9] rounded-2xl p-5" data-testid="price-breakdown">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Coins className="h-4 w-4 text-[#C5A059]" />
                  <span className="text-sm font-semibold text-[#2C2A29]">Live price breakdown</span>
                </div>
                {rate && (
                  p.purity === "Silver" ? (
                    <span className="text-xs text-[#8A8480]" data-testid="live-rate-label">
                      Today&apos;s silver rate: <span className="text-[#8B1E28] font-semibold">₹{Number(rate.silver_rate_per_gram || 0).toLocaleString("en-IN")}/g</span>
                    </span>
                  ) : (
                    <span className="text-xs text-[#8A8480]" data-testid="live-rate-label">
                      Today&apos;s 24k rate: <span className="text-[#8B1E28] font-semibold">₹{rate.rate_24k_per_gram.toLocaleString("en-IN")}/g</span>
                    </span>
                  )
                )}
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#595552]" data-testid="metal-line">
                    {p.purity === "Silver"
                      ? `Silver @ ₹${p.price_breakdown.rate_per_gram.toLocaleString("en-IN")}/g × ${p.weight}g`
                      : p.is_diamond
                        ? `18k gold @ ₹${p.price_breakdown.rate_per_gram.toLocaleString("en-IN")}/g × ${p.weight}g (net)`
                        : `${p.purity} gold @ ₹${p.price_breakdown.rate_per_gram.toLocaleString("en-IN")}/g × ${p.weight}g`}
                  </span>
                  <span>₹{p.price_breakdown.gold_value.toLocaleString("en-IN")}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#595552]">Making charges</span>
                  <span>₹{p.price_breakdown.making_charges.toLocaleString("en-IN")}</span>
                </div>
                {p.is_diamond && p.price_breakdown.diamond_mrp > 0 && (
                  <>
                    <div className="flex justify-between" data-testid="diamond-mrp-line">
                      <span className="text-[#595552]">
                        Diamond ({p.price_breakdown.diamond_weight_cts} ct) — MRP
                      </span>
                      <span className="text-[#8A8480] line-through">
                        ₹{p.price_breakdown.diamond_mrp.toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="flex justify-between items-center bg-[#FFF5F3] -mx-2 px-2 py-1.5 rounded-md" data-testid="diamond-discount-line">
                      <span className="text-[#8B1E28] font-medium">
                        Diamond discount ({p.price_breakdown.diamond_discount_pct}% off)
                      </span>
                      <span className="text-[#8B1E28] font-semibold">
                        − ₹{p.price_breakdown.diamond_saved.toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="flex justify-between" data-testid="diamond-effective-line">
                      <span className="text-[#595552]">Diamond (after discount)</span>
                      <span>₹{p.price_breakdown.diamond_effective.toLocaleString("en-IN")}</span>
                    </div>
                  </>
                )}
                {p.price_breakdown.stone_charges > 0 && (
                  <div className="flex justify-between">
                    <span className="text-[#595552]">Stone charges</span>
                    <span>₹{p.price_breakdown.stone_charges.toLocaleString("en-IN")}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-[#595552]">GST</span>
                  <span>₹{p.price_breakdown.gst.toLocaleString("en-IN")}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-[#EAE3D9] font-semibold">
                  <span>Total</span>
                  <span className="text-[#8B1E28]">₹{p.price.toLocaleString("en-IN")}</span>
                </div>
              </div>
            </div>
          )}

          {/* sizes */}
          {p.sizes?.length > 0 && (
            <div className="mt-7">
              <div className="label-small text-[#8A8480] mb-3">Size</div>
              <div className="flex flex-wrap gap-2">
                {p.sizes.map(s => (
                  <button key={s} onClick={() => setSize(s)}
                    className={`min-w-[50px] px-4 py-2 rounded-full text-sm border transition-colors ${size === s ? "bg-[#8B1E28] text-white border-[#8B1E28]" : "border-[#EAE3D9] text-[#2C2A29] hover:border-[#C5A059]"}`}
                    data-testid={`size-${s}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <p className="mt-7 text-[#595552] leading-relaxed whitespace-pre-line" data-testid="product-description">{p.description}</p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Button onClick={addToCart} disabled={adding}
              className="btn-primary rounded-full px-8 py-6 h-auto flex-1 sm:flex-none min-w-[200px]"
              data-testid="add-to-cart-btn">
              <ShoppingBag className="h-4 w-4 mr-2" /> {adding ? "Adding…" : "Add to Cart"}
            </Button>
            <Button onClick={buyNow}
              variant="outline" className="border-[#C5A059] text-[#8B1E28] hover:bg-[#FFF5F3] rounded-full px-8 py-6 h-auto"
              data-testid="buy-now-btn">
              Buy Now
            </Button>
            <Button onClick={toggleWish} variant="ghost"
              className="rounded-full h-12 w-12 p-0 border border-[#EAE3D9]"
              data-testid="product-wishlist-btn">
              <Heart className={`h-5 w-5 ${isWished ? "fill-[#8B1E28] text-[#8B1E28]" : ""}`} />
            </Button>
            <Button onClick={share} variant="ghost"
              className="rounded-full h-12 w-12 p-0 border border-[#EAE3D9]"
              title="Share this piece"
              data-testid="product-share-btn">
              {copied ? <Check className="h-5 w-5 text-emerald-600" /> : <Share2 className="h-5 w-5" />}
            </Button>
          </div>

          {/* Delivery check */}
          <div className="mt-8 p-5 rounded-2xl border border-[#EAE3D9] bg-white" data-testid="delivery-check">
            <div className="flex items-center gap-2 text-sm font-semibold text-[#2C2A29]">
              <MapPin className="h-4 w-4 text-[#8B1E28]" /> Check delivery to your pincode
            </div>
            <p className="text-xs text-[#8A8480] mt-1">Shipping is free on all prepaid orders. COD orders include courier + COD handling charges.</p>
            <div className="mt-3 flex gap-2">
              <input
                type="text" inputMode="numeric" maxLength={6}
                placeholder="Enter 6-digit pincode"
                value={pincode}
                onChange={(e) => setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); checkDelivery(); } }}
                className="flex-1 min-w-0 px-4 py-2 rounded-full border border-[#EAE3D9] focus:border-[#C5A059] outline-none text-sm"
                data-testid="delivery-pincode-input"
              />
              <button onClick={checkDelivery} disabled={checking}
                className="px-5 py-2 rounded-full text-sm font-semibold text-[#8B1E28] border border-[#C5A059] hover:bg-[#FFF5F3] disabled:opacity-60"
                data-testid="delivery-check-btn">
                {checking ? "Checking…" : "Check"}
              </button>
            </div>
            {deliveryErr && (
              <div className="mt-3 text-sm text-[#D32F2F]" data-testid="delivery-error">{deliveryErr}</div>
            )}
            {delivery && delivery.serviceable && (
              <div className="mt-4 text-sm space-y-1.5 bg-[#FFF5F3]/60 border border-[#EAE3D9] rounded-xl p-4" data-testid="delivery-result">
                <div className="flex items-center gap-2 text-[#2E7D32] font-semibold">
                  <Truck className="h-4 w-4" /> Deliverable
                </div>
                <div className="text-[#2C2A29]">
                  Expected delivery by <span className="font-semibold" data-testid="delivery-etd">{delivery.etd}</span>
                </div>
                <div className="text-xs text-[#595552] pt-1 border-t border-[#EAE3D9]">
                  Prepaid: <span className="font-semibold text-[#2E7D32]">Free shipping</span>
                </div>
                <div className="text-xs text-[#595552]" data-testid="delivery-cod-line">
                  COD: Courier ₹{delivery.freight_charges.toLocaleString("en-IN")}
                  {delivery.cod_charges > 0 && <> · Handling ₹{delivery.cod_charges.toLocaleString("en-IN")}</>}
                  {" "}· Total <span className="font-semibold">₹{delivery.total.toLocaleString("en-IN")}</span>
                </div>
              </div>
            )}
          </div>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 pt-8 border-t border-[#EAE3D9]">
            {(p.purity === "Silver" ? [
              { Icon: Shield, t: "925 Sterling", s: "Certified pure silver" },
              { Icon: Truck, t: "Free Delivery", s: "Insured shipping pan-India" },
              { Icon: RefreshCw, t: "Lifetime Exchange", s: "On all silver items" },
            ] : [
              { Icon: Shield, t: "BIS Hallmarked", s: "Certified pure gold" },
              { Icon: Truck, t: "Free Delivery", s: "Insured shipping pan-India" },
              { Icon: RefreshCw, t: "Lifetime Exchange", s: "On all gold items" },
            ]).map((f, i) => (
              <div key={i} className="flex items-start gap-3">
                <f.Icon className="h-5 w-5 text-[#C5A059] shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm font-semibold text-[#2C2A29]">{f.t}</div>
                  <div className="text-xs text-[#8A8480]">{f.s}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <ProductReviews productId={p.product_id} />
      <RelatedProducts productId={p.product_id} />
    </div>
  );
}
